'use client';
import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react';
import { useSession } from 'next-auth/react';
import { User as UserState, UserContextType } from '@/types/user';

const UserContext = createContext<UserContextType | undefined>(undefined);

export const defaultValues: UserState = {
	follow: [],
	hide: [],
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const { data: session, status } = useSession();
	const isLoggedIn = status === 'authenticated';

	const [user, setUser] = useState<UserState>(defaultValues);

	useEffect(() => {
		localStorage.setItem('user', JSON.stringify(user));
	}, [user]);

	useEffect(() => {
		if (status !== 'authenticated' || !session?.user?.id) return;

		const fetchApplications = async () => {
			try {
				const res = await fetch('/api/user', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ id: session.user.id }),
				});
				const data = await res.json();

				setUser({
					follow: data.follow ?? [],
					hide: data.hide ?? [],
				});
			} catch (error) {
				console.error('Error fetching user data', error);
			}
		};

		fetchApplications();
	}, [status, session?.user?.id]);

	useEffect(() => {
		if (status === 'unauthenticated') {
			setUser({ follow: [], hide: [] });
			localStorage.setItem(
				'user',
				JSON.stringify({
					follow: [],
					hide: [],
				})
			);
		}
	}, [status]);

	return (
		<UserContext.Provider
			value={{
				user,
				setUser,
				isLoggedIn,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

export const useUser = () => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUser must be used within a UserProvider');
	}
	return context;
};
